/**
 * The Activity page's filters (slice 2c spec §7.1, §8.1), kept in the URL so
 * a filtered list can be linked and survives a reload:
 * `/activity?status=failed&used=web&q=mulch`; and slice 6d's Area,
 * `?area=garage-gym` or `?area=none`.
 */
import { areaParam } from "../areas/area-view";

export const RANGES = ["today", "week", "month", "all"] as const;
const STATUSES = ["working", "failed", "unexpected"] as const;
export const STARTED_BY = [
  "person",
  "routine",
  "task",
  "approval",
  "nudge",
  "retry",
  "unexpected",
  "handoff",
] as const;
export const USED = ["web", "files", "tasks", "docs"] as const;

export type Range = (typeof RANGES)[number];
export type Status = (typeof STATUSES)[number];
export type StartedByKind = (typeof STARTED_BY)[number];
export type Used = (typeof USED)[number];

export type Filters = {
  range: Range;
  status: Status | null;
  agents: string[];
  startedBy: StartedByKind[];
  used: Used[];
  q: string;
  /** An Area's slug, `none`, or null for all (slice 6d spec §4.4). */
  area: string | null;
};

export const DEFAULT_FILTERS: Filters = {
  range: "week",
  status: null,
  agents: [],
  startedBy: [],
  used: [],
  q: "",
  area: null,
};

const pick = <T extends string>(raw: string | null, allowed: readonly T[]): T | null =>
  raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : null;

const many = <T extends string>(raw: string | null, allowed: readonly T[] | null): T[] =>
  [
    ...new Set(
      (raw ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ].filter((s) => !allowed || (allowed as readonly string[]).includes(s)) as T[];

/** The filters in a URL's search; anything unknown is dropped, never an error. */
export function filtersFrom(search: URLSearchParams): Filters {
  return {
    range: pick(search.get("range"), RANGES) ?? DEFAULT_FILTERS.range,
    status: pick(search.get("status"), STATUSES),
    agents: many(search.get("agent"), null),
    startedBy: many(search.get("started_by"), STARTED_BY),
    used: many(search.get("used"), USED),
    q: (search.get("q") ?? "").slice(0, 200),
    area: areaParam(search.get("area")),
  };
}

/** The search for `f`: only what differs from the defaults, in a fixed order. */
export function searchFor(f: Filters): URLSearchParams {
  const s = new URLSearchParams();
  if (f.range !== DEFAULT_FILTERS.range) s.set("range", f.range);
  if (f.status) s.set("status", f.status);
  if (f.agents.length) s.set("agent", f.agents.join(","));
  if (f.startedBy.length) s.set("started_by", f.startedBy.join(","));
  if (f.used.length) s.set("used", f.used.join(","));
  if (f.q.trim().length >= 2) s.set("q", f.q.trim());
  if (f.area) s.set("area", f.area);
  return s;
}

/** Whether anything beyond the range and status narrows the list: shows "Clear". */
export function narrowed(f: Filters): boolean {
  return f.agents.length + f.startedBy.length + f.used.length > 0 || f.q.trim().length >= 2;
}

/** `list` with `value` added, or taken out when it's there. */
export function toggled<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}
